/** Tool ids rendered by {@link TextFormatToolbar}. */
export const TEXT_FORMAT_TOOLS = {
  FONT: "font",
  SIZE: "size",
  COLOR: "color",
  BOLD: "bold",
  ITALIC: "italic",
  UNDERLINE: "underline",
  STRIKE: "strike",
  CASE: "case",
  SPACING: "spacing",
  ALIGN: "align",
  EFFECT: "effect",
  OPACITY: "opacity",
  POSITION: "position",
};

/** Default universal text-style shape (host maps its own fields). */
export const DEFAULT_TEXT_FORMAT = {
  fontId: "Arial",
  size: 4.5,
  color: "#333333",
  fontWeight: "normal",
  fontStyle: "normal",
  underline: false,
  strikethrough: false,
  textAlign: "center",
  /** Tracking: hundredths of an em (−100…200 → −1em…2em). */
  letterSpacing: 0,
  /** Unitless line-height multiplier. */
  lineHeight: 1.4,
  /** Vertical anchor inside the text box: top | middle | bottom */
  textVerticalAlign: "top",
  /** normal | super | sub */
  textScript: "normal",
  /** none | shadow | outline | glow */
  effect: "none",
  opacity: 100,
  position: "center",
  offsetX: 0,
  offsetY: 0,
  rotation: 0,
  /** Tile layout — host maps these (grid / diamond Position dropdown) */
  spacing: 0.5,
  margin: 0.08,
  tile: "single",
  lockAspectRatio: true,
};

export const TEXT_ALIGN_OPTIONS = [
  { id: "justify", label: "Justify" },
  { id: "left", label: "Align left" },
  { id: "right", label: "Align right" },
  { id: "center", label: "Align center" },
];

/**
 * Host-specific align menu rules. Default: all options enabled (toolbar stays reusable).
 * @typedef {{ enabledIds?: string[], disabledIds?: string[], defaultId?: string }} TextAlignConfig
 *
 * Watermark Maker is single-line — only Justify is interactive.
 */
export const WATERMARK_ALIGN_CONFIG = {
  enabledIds: ["justify"],
  defaultId: "justify",
  disabledTip: "Not available for single-line watermarks",
};

/**
 * Host-specific advanced spacing panel rules.
 * Default: all sections shown & enabled (toolbar stays reusable).
 * Watermark Maker is single-line — line height & anchor stay visible but disabled.
 */
export const WATERMARK_SPACING_CONFIG = {
  showLetterSpacing: true,
  showLineHeight: true,
  lineHeightDisabled: true,
  lineHeightDisabledTip: "Line spacing isn’t available for single-line watermarks",
  showAnchor: true,
  anchorDisabled: true,
  anchorDisabledTip: "Text-box anchor isn’t available for single-line watermarks",
  showTextPosition: true,
};

/** Whether an align option should accept clicks for the given host config. */
export function isAlignOptionEnabled(optionId, alignConfig = {}) {
  const { enabledIds, disabledIds } = alignConfig;
  if (Array.isArray(enabledIds) && enabledIds.length > 0) {
    return enabledIds.includes(optionId);
  }
  if (Array.isArray(disabledIds) && disabledIds.length > 0) {
    return !disabledIds.includes(optionId);
  }
  return true;
}

export const TEXT_VERTICAL_ALIGN_OPTIONS = [
  { id: "top", label: "Top" },
  { id: "middle", label: "Middle" },
  { id: "bottom", label: "Bottom" },
];

export const TEXT_SCRIPT_OPTIONS = [
  { id: "normal", label: "Normal", base: "A", exp: "2", expPos: "normal" },
  { id: "super", label: "Superscript", base: "A", exp: "2", expPos: "super" },
  { id: "sub", label: "Subscript", base: "A", exp: "2", expPos: "sub" },
];

export const TEXT_CASE_OPTIONS = [
  { id: "upper", label: "Uppercase", short: "AA" },
  { id: "lower", label: "Lowercase", short: "aa" },
  { id: "camel", label: "Title case", short: "Aa" },
  { id: "sentence", label: "Sentence case", short: "Sa" },
];

/** Toolbar effect menu (no "None" — click active again to clear). */
export const TEXT_EFFECT_OPTIONS = [
  { id: "shadow", label: "Shadow" },
  { id: "outline", label: "Outline" },
  { id: "glow", label: "Glow" },
];

/** Display numbers shown in the size dropdown (host maps via parseDisplay). */
export const DEFAULT_SIZE_DISPLAY_OPTIONS = [
  8, 9, 10, 11, 12, 14, 16, 18,
  // Dense steps through the common mid range
  20, 22, 24, 26, 28, 30, 32, 34, 36, 38, 40, 42, 44, 46, 48, 50,
  52, 54, 56, 58, 60, 62, 64, 66, 68, 70, 72, 74, 76, 78, 80,
  82, 84, 86, 88, 90, 92, 94, 96, 98, 100,
  // Larger sizes (px display → internal via parseDisplay)
  110, 120, 140, 160, 180, 200, 220, 240,
];

export function resolveSizeMenuOptions(sizeConfig = {}) {
  const min = sizeConfig.min ?? 0;
  const max = sizeConfig.max ?? 24;
  const parseDisplay =
    sizeConfig.parseDisplay ??
    ((display) => {
      const n = Number(display);
      return Number.isFinite(n) ? n : min;
    });
  const source = sizeConfig.options ?? DEFAULT_SIZE_DISPLAY_OPTIONS;

  return source
    .map((display) => ({
      display: String(display),
      internal: parseDisplay(display),
    }))
    .filter(({ internal }) => internal >= min && internal <= max);
}

/** Preset tool sets for common hosts. */
export const TEXT_FORMAT_TOOL_SETS = {
  editorWatermarkText: [
    TEXT_FORMAT_TOOLS.FONT,
    TEXT_FORMAT_TOOLS.SIZE,
    TEXT_FORMAT_TOOLS.COLOR,
    TEXT_FORMAT_TOOLS.BOLD,
    TEXT_FORMAT_TOOLS.ITALIC,
    TEXT_FORMAT_TOOLS.UNDERLINE,
    TEXT_FORMAT_TOOLS.STRIKE,
    TEXT_FORMAT_TOOLS.CASE,
    TEXT_FORMAT_TOOLS.SPACING,
    TEXT_FORMAT_TOOLS.ALIGN,
    TEXT_FORMAT_TOOLS.EFFECT,
    TEXT_FORMAT_TOOLS.OPACITY,
    TEXT_FORMAT_TOOLS.POSITION,
  ],
  editorWatermarkLogo: [
    TEXT_FORMAT_TOOLS.SIZE,
    TEXT_FORMAT_TOOLS.OPACITY,
  ],
};
